import { FieldValues, useController } from "react-hook-form";
import styled, { css } from "styled-components";

import { SwitchLabel, SwitchSlider } from "@/components/Switch";
import Text from "@/components/Text";
import { DefaultProps, SwitchFieldProps } from "../FormField";

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Description = styled(Text)<{ $withDivider: boolean }>`
  padding-right: 40px;
  margin-top: 5px;

  ${({ $withDivider, theme }) =>
    $withDivider &&
    css`
      padding-right: 0;
      padding-top: 16px;
      margin-top: 16px;
      border-top: 0.5px solid ${theme.colors.lightGray};
    `}
`;

const Field = styled.div`
  padding: 18px 12px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
`;

type Props<T extends FieldValues> = Omit<
  DefaultProps<T> & SwitchFieldProps,
  "type"
>;

const SwitchField = <T extends FieldValues>({
  name,
  control,
  description,
  headline,
  className,
  disabled = false,
  id,
}: Props<T>) => {
  const {
    field: { onChange, value },
  } = useController({ name, control });

  return (
    <Field>
      <Header>
        <Text $type="smallHeadline">{headline}</Text>
        <SwitchLabel className={className}>
          <input
            id={id}
            type="checkbox"
            disabled={disabled}
            checked={value}
            onChange={() => onChange(!value)}
          />
          <SwitchSlider $disabled={disabled} />
        </SwitchLabel>
      </Header>
      {!!description && (
        <Description
          $color="gray"
          $type="smallNormal"
          $withDivider={description.length > 104}
        >
          {description}
        </Description>
      )}
    </Field>
  );
};

export default SwitchField;
